import type { FunctionComponent } from 'react';
import React, { Suspense, useCallback, useState } from 'react';

import cx from 'classnames';
import { forTemplate } from '@trello/i18n';
import { DynamicButton } from '@trello/dynamic-tokens';
import { PowerUpIcon } from '@trello/nachos/icons/power-up';
import type { ButtonProps } from '@trello/nachos/button';
import { Button } from '@trello/nachos/button';
import { usePopover, Popover } from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useLazyComponent } from '@trello/use-lazy-component';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { WarningIcon } from '@trello/nachos/icons/warning';

import styles from './PluginListButton.less';
import type { Board } from './PluginHeaderButton.types';
import { useHasDeprecatedPlugins } from './useHasDeprecatedPlugins';

const format = forTemplate('board_header_pup_dropdown');

interface PluginListButtonProps {
  openDirectory: () => object;
  openEnabled: () => object;
  openPluginSettings: () => object;
  board: Board;
  container: Element;
}

export const PluginListButton: FunctionComponent<PluginListButtonProps> = ({
  openDirectory,
  openEnabled,
  openPluginSettings,
  board,
  container,
}: PluginListButtonProps) => {
  const PluginListPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "plugin-list-popover" */ 'app/src/components/PluginHeaderButton'
      ),
    {
      namedImport: 'PluginListPopover',
    },
  );

  const numPlugins = board?.powerUpsCount();
  const idBoard = board.id;
  const idOrganization = board?.attributes?.idOrganization;

  const isDeprecationEnabled = useFeatureFlag(
    'ecosystem.pup-deprecation-field',
    false,
  );
  const hasDeprecatedPlugins = useHasDeprecatedPlugins(board);

  const [traceId, setTraceId] = useState('');

  const openDirectoryCallback = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'pluginListAddPowerUpsButton',
      source: 'powerUpHubPopover',
      containers: formatContainers({
        idBoard,
        idOrganization,
      }),
    });
    openDirectory();
  }, [openDirectory, idOrganization, idBoard]);

  const startTrace = useCallback(() => {
    setTraceId(
      Analytics.startTask({
        taskName: 'view-power-up-hub',
        source: 'powerUpHubPopover',
      }),
    );
  }, []);

  const { triggerRef, toggle, popoverProps } = usePopover<HTMLButtonElement>({
    onShow: startTrace,
  });

  const openPluginList = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'pluginListButton',
      source: 'boardScreen',
      containers: formatContainers({
        idBoard,
        idOrganization,
      }),
    });
    Analytics.sendScreenEvent({
      name: 'powerUpsMenuInlineDialog',
    });
    toggle();
  }, [toggle, idBoard, idOrganization]);

  let deprecatedPUpsWarningIcon: ButtonProps['iconAfter'];

  if (isDeprecationEnabled && hasDeprecatedPlugins) {
    deprecatedPUpsWarningIcon = (
      <WarningIcon dangerous_className={styles.deprecatedPluginsWarningIcon} />
    );
  }

  return (
    <div>
      <DynamicButton
        className={styles.pluginDropdownButton}
        ref={triggerRef}
        iconBefore={<PowerUpIcon size="small" />}
        iconAfter={deprecatedPUpsWarningIcon}
        onClick={openPluginList}
      >
        {format('power-ups')}
      </DynamicButton>
      <Popover
        dangerous_className={styles.removePopoverPadding}
        title={format('power-ups')}
        {...popoverProps}
      >
        <Suspense fallback={<Spinner centered />}>
          <PluginListPopover
            board={board}
            container={container}
            openDirectory={openDirectory}
            openEnabled={openEnabled}
            openPluginSettings={openPluginSettings}
            toggle={toggle}
            traceId={traceId}
          />
          {board.editable() ? (
            <div
              className={cx(
                styles.footerBox,
                numPlugins === 0 && styles.noPlugins,
              )}
            >
              <Button
                appearance={'primary'}
                shouldFitContainer={true}
                onClick={openDirectoryCallback}
              >
                {format('add-power-ups')}
              </Button>
            </div>
          ) : null}
        </Suspense>
      </Popover>
    </div>
  );
};
